/**
 * Tenant-scoped localStorage helper.
 *
 * Enforces KBP-21 (severity=blocking, scope=tenant): localStorage keys MUST be
 * prefixed with tenant + user so they don't collide across tenants in the same
 * browser session. Pattern: `tenant_{tenantId}_user_{userId|anon}_<logicalKey>`.
 *
 * - tenantId comes from CURRENT_BRAND.id (the build-time tenant identity).
 * - userId is optional; defaults to "anon" for pre-login preferences and for
 *   the wizard sessionId (which exists before Cognito identity is confirmed).
 *
 * Always use scopedStorage instead of direct localStorage.* calls.
 */
import { CURRENT_BRAND } from "@/brand/config";

const TENANT = CURRENT_BRAND.id;
const ANON_USER = "anon";

export const STORAGE_KEYS = {
  colorScheme: "color-scheme",
  themeMode: "theme-mode",
  sessionId: "session-id",
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

export interface StorageScope {
  userId?: string;
}

function scopedKey(key: StorageKey, scope?: StorageScope): string {
  const u = scope?.userId || ANON_USER;
  return `tenant_${TENANT}_user_${u}_${key}`;
}

export const scopedStorage = {
  get(key: StorageKey, scope?: StorageScope): string | null {
    try {
      return localStorage.getItem(scopedKey(key, scope));
    } catch {
      return null;
    }
  },
  set(key: StorageKey, value: string, scope?: StorageScope): void {
    try {
      localStorage.setItem(scopedKey(key, scope), value);
    } catch {
      // Quota exceeded or storage disabled; swallow.
    }
  },
  remove(key: StorageKey, scope?: StorageScope): void {
    try {
      localStorage.removeItem(scopedKey(key, scope));
    } catch {
      // ignore
    }
  },
  /**
   * Remove every key belonging to the current tenant. Use on global sign-out
   * or tenant switch within the same browser session.
   */
  clearTenant(): void {
    try {
      const prefix = `tenant_${TENANT}_`;
      const toDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) toDelete.push(k);
      }
      toDelete.forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  },
};

/**
 * One-time migration of pre-KBP-21 unscoped keys (synapta-onboard-*) into the
 * tenant-scoped namespace. Idempotent: subsequent calls are no-ops.
 *
 * Call once at boot (main.tsx) BEFORE any module that reads localStorage,
 * so users don't lose their color-scheme / theme-mode preferences.
 */
export function migrateLegacyStorageKeys(): void {
  try {
    const mapping: Record<string, StorageKey> = {
      "synapta-onboard-color-scheme": STORAGE_KEYS.colorScheme,
      "synapta-onboard-theme-mode": STORAGE_KEYS.themeMode,
      "synapta-onboard-session-id": STORAGE_KEYS.sessionId,
    };

    for (const [legacyKey, newKey] of Object.entries(mapping)) {
      const val = localStorage.getItem(legacyKey);
      if (val !== null) {
        const target = scopedKey(newKey);
        if (localStorage.getItem(target) === null) {
          localStorage.setItem(target, val);
        }
        localStorage.removeItem(legacyKey);
      }
    }

    // Sweep any leftover legacy prefix in case env var overrode the default.
    const legacyPrefixes = ["synapta-onboard-"];
    for (const prefix of legacyPrefixes) {
      const toDelete: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) toDelete.push(k);
      }
      toDelete.forEach((k) => localStorage.removeItem(k));
    }
  } catch {
    // ignore
  }
}
